import React from "react";
import { Box, Grid, Typography, Skeleton } from "@mui/material";
import {
  ShoppingCart,
  TrendingUp,
  CallMade,
  CallReceived,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";

const StatCard = ({ title, value, icon, gradient, subtitle, loading, trend }) => (
  <Box
    sx={{
      background: "#fff",
      borderRadius: "18px",
      padding: "22px 24px",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      width: "100%",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.04)",
      boxShadow: "0 1px 3px rgba(0,0,0,0.02), 0 6px 16px rgba(0,0,0,0.02)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        boxShadow: "0 4px 12px rgba(0,0,0,0.06), 0 12px 28px rgba(0,0,0,0.04)",
        transform: "translateY(-2px)",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: gradient,
        borderRadius: "18px 18px 0 0",
      },
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 500,
          color: "#8896ab",
          letterSpacing: "0.02em",
          lineHeight: 1.2,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {loading ? (
        <Skeleton width={50} height={36} sx={{ borderRadius: "8px" }} />
      ) : (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "2rem",
              fontWeight: 800,
              color: "#0f172a",
              lineHeight: 1,
              letterSpacing: "-0.02em",
            }}
          >
            {value ?? 0}
          </Typography>
          {trend && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.2,
                padding: "2px 6px",
                borderRadius: "6px",
                backgroundColor: trend > 0 ? "#ecfdf5" : "#fef2f2",
              }}
            >
              {trend > 0 ? (
                <ArrowUpward sx={{ fontSize: 10, color: "#10b981" }} />
              ) : (
                <ArrowDownward sx={{ fontSize: 10, color: "#ef4444" }} />
              )}
              <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: trend > 0 ? "#10b981" : "#ef4444" }}>
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {subtitle && !loading && (
        <Typography
          sx={{
            fontSize: "0.65rem",
            color: "#a0aec0",
            mt: 0.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "14px",
        background: gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 22, color: "#fff" } })}
    </Box>
  </Box>
);

const StatsCards = ({ data, loading }) => {
  const prevMonth = data?.last_month_movements;
  const currMonth = data?.current_movements;
  const trend = prevMonth && prevMonth > 0
    ? Math.round(((currMonth - prevMonth) / prevMonth) * 100)
    : null;

  const cards = [
    {
      title: "Pedidos del mes",
      value: data?.current_movements,
      icon: <ShoppingCart />,
      gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
      trend: trend,
      subtitle: prevMonth != null ? `Mes anterior: ${prevMonth}` : null,
    },
    {
      title: "Acumulado anual",
      value: data?.yearly_movements,
      icon: <TrendingUp />,
      gradient: "linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)",
    },
    {
      title: "Pendiente egreso",
      value: data?.pending_movements_out,
      icon: <CallMade />,
      gradient: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
    },
    {
      title: "Pendiente ingreso",
      value: data?.pending_movements,
      icon: <CallReceived />,
      gradient: "linear-gradient(135deg, #ec4899 0%, #f472b6 100%)",
    },
  ];

  return (
    <Grid container spacing={3}>
      {cards.map((card, i) => (
        <Grid item xs={6} sm={6} md={3} key={i}>
          <StatCard {...card} loading={loading} />
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;
